import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient'; // Your Supabase client
import { handleSubscribe } from './utils/subscriptionUtils'; // Import the shared function
import { AuthContext } from './AuthContext'; // Import AuthContext
import {
  Container, Box, Typography, Button, CircularProgress, Alert, Paper, TextField, Grid, Collapse
} from '@mui/material';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function CreateCompanyForm() {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext); // Get currentUser from context
  const [companyName, setCompanyName] = useState('');
  
  // State for optional fields
  const [billingAddressLine1, setBillingAddressLine1] = useState('');
  const [billingAddressLine2, setBillingAddressLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingPostalCode, setBillingPostalCode] = useState('');
  const [billingCountry, setBillingCountry] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [taxDepartment, setTaxDepartment] = useState('');
  // company_status is usually system-set, not user input in this form

  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Optional: Prefill or check existing company status if needed
  useEffect(() => {
    const checkUserCompany = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        if (profileError && profileError.code !== 'PGRST116') {
            setError("Could not verify user profile. Please try again.");
            return;
        }

        if (profile && profile.company_id) {
          const { data: company, error: companyError } = await supabase
            .from('companies')
            .select('name')
            .eq('company_id', profile.company_id)
            .single();
          if (companyError) {
            // Handle case where company might not be found, though profile has company_id
            console.warn("Error fetching company details for pre-check:", companyError);
            return;
          }
          if (company && !company.name.startsWith('Personal Workspace for')) {
            setMessage(`You are already part of ${company.name}. Redirecting...`);
            setTimeout(() => navigate('/fileupload'), 2000);
          }
        }
      }
    };
    checkUserCompany();
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!companyName.trim()) {
      setError('Company name is required.');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('User session not found. Please log in.');
      }
      const token = session.access_token;
      const apikey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;

      const payload = {
        company_name: companyName.trim(),
        // Add optional fields to payload only if they have values
        ...(billingAddressLine1.trim() && { billing_address_line1: billingAddressLine1.trim() }),
        ...(billingAddressLine2.trim() && { billing_address_line2: billingAddressLine2.trim() }),
        ...(billingCity.trim() && { billing_city: billingCity.trim() }),
        ...(billingState.trim() && { billing_state: billingState.trim() }),
        ...(billingPostalCode.trim() && { billing_postal_code: billingPostalCode.trim() }),
        ...(billingCountry.trim() && { billing_country: billingCountry.trim() }),
        ...(taxNumber.trim() && { tax_number: taxNumber.trim() }),
        ...(taxDepartment.trim() && { tax_department: taxDepartment.trim() }),
      };

      const response = await fetch(`${BACKEND_URL}/functions/v1/onboarding-create-company-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'apikey': apikey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create company profile.');
      }

      setMessage(result.message || `Company '${result.name}' created/updated successfully!`);
      
      // --- NEW: Check for pending subscription after company creation ---
      const pendingPriceId = localStorage.getItem('pendingSubscriptionPriceId');
      if (pendingPriceId) {
        console.log(`[CreateCompanyForm] Company created (ID: ${result.company_id}), now processing pending subscription.`);
        localStorage.removeItem('pendingSubscriptionPriceId');
        localStorage.removeItem('pendingSubscriptionPackageId'); // Clean up both
        
        // Use the shared subscribe function, passing the newly created company ID and the current user's ID
        await handleSubscribe(pendingPriceId, currentUser.id, result.company_id, setError, setLoading);
        // The handleSubscribe function will handle the redirect, so we don't navigate away here.
        // If it fails, the error will be set and displayed on this page.
      } else {
        // If no pending subscription, direct the user to the pricing page to choose a plan.
        setTimeout(() => navigate('/pricing'), 5000); 
      }

    } catch (err) {
      console.error('Error creating company profile:', err);
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="lg" sx={{ py: 6 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
              Set Up Your Company
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please provide a name for your company. Other details are optional and can be added later.
            </Typography>

            {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{message}</Alert>}

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="companyName"
                label="Company Name"
                name="companyName"
                autoComplete="organization"
                autoFocus
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={loading}
              />

              <Button 
                onClick={() => setShowOptionalFields(!showOptionalFields)}
                sx={{ mt: 2, mb: 1, textTransform: 'none' }}
                size="small"
              >
                {showOptionalFields ? 'Hide Optional Details' : 'Add More Company Details (Optional)'}
              </Button>

              <Collapse in={showOptionalFields}>
                <Typography variant="caption" display="block" gutterBottom sx={{mt:1}}>
                  These details can be updated later from your company settings.
                </Typography>
                <TextField margin="normal" fullWidth id="billingAddressLine1" label="Billing Address Line 1" name="billingAddressLine1" value={billingAddressLine1} onChange={(e) => setBillingAddressLine1(e.target.value)} disabled={loading} />
                <TextField margin="normal" fullWidth id="billingAddressLine2" label="Billing Address Line 2 (Optional)" name="billingAddressLine2" value={billingAddressLine2} onChange={(e) => setBillingAddressLine2(e.target.value)} disabled={loading} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField margin="normal" fullWidth id="billingCity" label="Billing City" name="billingCity" value={billingCity} onChange={(e) => setBillingCity(e.target.value)} disabled={loading} /></Grid>
                  <Grid item xs={12} sm={6}><TextField margin="normal" fullWidth id="billingState" label="Billing State/Province" name="billingState" value={billingState} onChange={(e) => setBillingState(e.target.value)} disabled={loading} /></Grid>
                  <Grid item xs={12} sm={6}><TextField margin="normal" fullWidth id="billingPostalCode" label="Billing Postal Code" name="billingPostalCode" value={billingPostalCode} onChange={(e) => setBillingPostalCode(e.target.value)} disabled={loading} /></Grid>
                  <Grid item xs={12} sm={6}><TextField margin="normal" fullWidth id="billingCountry" label="Billing Country" name="billingCountry" value={billingCountry} onChange={(e) => setBillingCountry(e.target.value)} disabled={loading} /></Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField margin="normal" fullWidth id="taxNumber" label="Tax Number" name="taxNumber" value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} disabled={loading} /></Grid>
                  <Grid item xs={12} sm={6}><TextField margin="normal" fullWidth id="taxDepartment" label="Tax Department (Optional)" name="taxDepartment" value={taxDepartment} onChange={(e) => setTaxDepartment(e.target.value)} disabled={loading} /></Grid>
                </Grid>
              </Collapse>

              <Button type="submit" fullWidth variant="contained" color="primary" disabled={loading} sx={{ mt: 3, mb: 2, py: 1.5 }}>
                {loading ? <CircularProgress size={24} /> : 'Save and Continue'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 2, height: { xs: 320, md: 560 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ position: 'relative', width: '100%', maxWidth: 520 }}>
              <Box component="img" src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=1200&auto=format&fit=crop" alt="Company setup" sx={{ width: '100%', borderRadius: 2, display: 'block' }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default CreateCompanyForm; 